/* RealU Coach intake — thin shell.
   State + logic live in hooks (useFlow via SurveyContext, useKeyboardNav,
   useAutoScroll); each screen is its own component resolved through the step
   registry. This file just lays out the chrome (top dots / scrolling thread or
   centered page / nav) and wires the side effects. */
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { SurveyProvider, useSurvey } from './SurveyContext';
import { useKeyboardNav } from './hooks/useKeyboardNav';
import { useAutoScroll } from './hooks/useAutoScroll';
import { Icon } from './icons';
import { Confetti } from './Confetti';
import { CHAPTERS, Step } from './flow';
import { bubbleContent, summaryText } from './stepText';
import { StepControls } from './steps/registry';
import { HABITS } from './data';

// Same RealityU logo the admin pages show top-left (layouts/base), in the
// color variant since the survey header is light (admin nav uses _white).
const PLOGO = '/images/RU_powered_by_color.png';

// ---- chapter progress dots --------------------------------------------
function ChapterDots() {
  const surveyFlow = useSurvey();
  const order = CHAPTERS.map((c) => c.id);
  const curCh = surveyFlow.current.kind === 'review' ? 'done' : surveyFlow.current.chapter;
  const ci = order.indexOf(curCh);
  return (
    <div className="intake-dots">
      {CHAPTERS.map((c, i) => {
        const firstStep = surveyFlow.steps.findIndex((s) => s.chapter === c.id);
        const reachable = firstStep >= 0 && firstStep <= surveyFlow.highWater;
        const state = curCh === 'done' ? 'done' : i === ci ? 'active' : reachable ? 'done' : 'todo';
        return (
          <div
            className="intake-d"
            key={c.id}
            data-state={state}
            onClick={() => {
              if (!surveyFlow.isPage && state !== 'todo') surveyFlow.goToChapter(c.id);
            }}
          >
            <span className="intake-dot" />
            <span className="intake-lab">{c.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ---- coach bubble (question) ------------------------------------------
function Coach({ step, size, onClick }: { step: Step; size: number; onClick?: () => void }) {
  return (
    <div className="intake-coach" onClick={onClick}>
      <div className="intake-avatar">
        <img src="/images/icons/RU_Icon.png" width={size} height={size} alt="RealU" />
      </div>
      <div className="intake-bubble">
        <div className="intake-btext">{bubbleContent(step)}</div>
      </div>
    </div>
  );
}

// ---- one turn in the thread (coach + answer or collapsed summary) ------
function Turn({ step, i, active }: { step: Step; i: number; active: boolean }) {
  const surveyFlow = useSurvey();
  const sum = active ? '' : summaryText(step, surveyFlow.answers);
  return (
    <div className="intake-turn" data-active={active ? 'true' : 'false'}>
      <Coach step={step} size={24} onClick={active || surveyFlow.isPage ? undefined : () => surveyFlow.goTo(i)} />
      {active ? (
        <div className="intake-answers">
          <StepControls step={step} />
        </div>
      ) : sum ? (
        <div className="intake-user">
          <div className="intake-ubub">{sum}</div>
        </div>
      ) : null}
    </div>
  );
}

// ---- bottom nav -------------------------------------------------------
function NavBar({ onFinish, submitPending }: { onFinish: () => void; submitPending: boolean }) {
  const surveyFlow = useSurvey();
  let nextLabel: React.ReactNode = (
    <>
      Continue <Icon name="arrowR" size={18} />
    </>
  );
  if (surveyFlow.current.kind === 'welcome')
    nextLabel = (
      <>
        Let&apos;s go <Icon name="arrowR" size={18} />
      </>
    );
  else if (surveyFlow.current.kind === 'score')
    nextLabel = (
      <>
        Pick my career <Icon name="arrowR" size={18} />
      </>
    );
  else if (surveyFlow.current.kind === 'careerPick')
    nextLabel = submitPending ? (
      <svg className="intake-spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
        <path d="M12 2a10 10 0 0 1 10 10" />
      </svg>
    ) : (
      <>
        Finish <Icon name="arrowR" size={18} />
      </>
    );

  return (
    <div className="intake-nav">
      {surveyFlow.canGoBack ? (
        <button type="button" className="intake-btn intake-ghost intake-back" onClick={surveyFlow.back}>
          <Icon name="arrowL" size={18} /> Back
        </button>
      ) : null}
      <span className="intake-navspace" />
      {surveyFlow.current.kind !== 'review' ? (
        <button
          type="button"
          className="intake-btn intake-primary intake-next"
          disabled={!surveyFlow.canAdvance || (surveyFlow.current.kind === 'careerPick' && submitPending)}
          onClick={surveyFlow.current.kind === 'careerPick' ? onFinish : surveyFlow.doNext}
        >
          {nextLabel}
        </button>
      ) : null}
    </div>
  );
}

// ---- shell: lays out the chrome + wires side effects ------------------
function SurveyShell() {
  const surveyFlow = useSurvey();
  const mainRef = useRef<HTMLDivElement>(null);
  const finishedRef = useRef(false);
  const [showConfetti, setShowConfetti] = React.useState(false);
  const [submitPending, setSubmitPending] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');

  useKeyboardNav({
    doNext: surveyFlow.doNext,
    back: surveyFlow.back,
    canGoBack: surveyFlow.canGoBack,
    selectIndex: surveyFlow.selectIndex,
  });
  useAutoScroll(mainRef, surveyFlow.idx, surveyFlow.isPage, surveyFlow.current.kind);

  useEffect(() => {
    if (surveyFlow.current.kind === 'review' && !finishedRef.current) {
      finishedRef.current = true;
      setShowConfetti(true);
    }
  }, [surveyFlow.current.kind]);

  // the gender error is resolved once they pick one and head back to careers
  useEffect(() => {
    if (!surveyFlow.fixingGender) setSubmitError('');
  }, [surveyFlow.fixingGender]);

  async function handleFinish() {
    const csrf = document.querySelector('meta[name="_csrf"]')?.getAttribute('content') || '';
    const answers = surveyFlow.answers;
    const effortAnswers = HABITS
      .filter(h => typeof answers.habits[h.id] === 'number')
      .map(h => {
        const idx = answers.habits[h.id];
        // optionIndex identifies WHICH answer was chosen - unlike points it
        // survives later admin point edits/rebalancing, so viewers can always
        // reconstruct the selection.
        return { id: h.id, points: h.options[idx]?.points ?? 0, optionIndex: idx };
      });
    setSubmitPending(true);
    setSubmitError('');
    try {
      const payload = JSON.stringify({
        eventId: answers.eventId,
        firstName: answers.firstName,
        lastName: answers.lastName,
        gpa: answers.gpa,
        gender: answers.gender,
        teacherId: answers.teacherId,
        sessionId: answers.sessionId,
        career: answers.career,
        maritalStatus: answers.maritalStatus,
        kidsCount: answers.kidsCount,
        kidsAges: answers.kidsAges,
        pets: answers.pets,
        effortAnswers,
      });
      const resp = await fetch('/lifestyle-next', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'x-csrf-token': csrf,
        },
        body: new URLSearchParams({ data: payload }),
      });
      const data = await resp.json();
      if (data.ok) {
        surveyFlow.doNext();
      } else {
        setSubmitError(data.error || 'Something went wrong — please try again.');
        // send them back to the name & gender step to pick one
        if (data.field === 'gender') surveyFlow.fixGender();
      }
    } catch {
      setSubmitError('Something went wrong — please try again.');
    } finally {
      setSubmitPending(false);
    }
  }

  return (
    <div className="intake">
      {showConfetti && <Confetti />}
      <div className="intake-top">
        <img
          className="intake-plogo"
          src={PLOGO}
          style={{ width: 175, height: 'auto' }}
          alt="Pando Initiative"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
        <ChapterDots />
      </div>

      <div className={'intake-main ' + (surveyFlow.isPage ? 'intake-page-mode' : 'intake-thread-mode')} ref={mainRef}>
        {surveyFlow.isPage ? (
          <div className="intake-conv">
            <Coach step={surveyFlow.current} size={26} />
            <div className="intake-answers">
              <StepControls step={surveyFlow.current} />
            </div>
          </div>
        ) : (
          <div className="intake-thread">
            {surveyFlow.steps.slice(0, surveyFlow.idx + 1).map((s, i) => (
              <Turn key={s.key} step={s} i={i} active={i === surveyFlow.idx} />
            ))}
          </div>
        )}
      </div>

      <NavBar onFinish={handleFinish} submitPending={submitPending} />
      {submitError ? (
        <div style={{ position: 'absolute', bottom: 72, left: 0, right: 0, textAlign: 'center', color: '#a13333', fontWeight: 600, fontSize: 14, padding: '0 24px' }}>
          {submitError}
        </div>
      ) : null}
    </div>
  );
}

export default function SurveyApp() {
  return (
    <SurveyProvider>
      <SurveyShell />
    </SurveyProvider>
  );
}
